/**
 * Coach Client Roster — Full list of active clients for coach_pro tier.
 *
 * Route: /coach-client-roster?orgId=<coachOrgId>
 * Registered in app/_layout.tsx.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity,
  TextInput, ActivityIndicator, RefreshControl,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { listMyClientLinks, resolveProfileById, type TrainerClientLink } from '@/lib/trainers';
import { ArrowLeft, UserPlus, Users, Search } from 'lucide-react-native';
import { TYPO } from '@/constants/typography';
import ClientCard, { type ClientCardData } from '@/components/coach/ClientCard';
import ClientInvite from '@/components/coach/ClientInvite';
import { useCoachAccess } from '@/lib/coachAccess';

export default function CoachClientRoster() {
  const { theme } = useTheme();
  const c = theme.colors;
  const router = useRouter();
  const { profile } = useAuth();
  const params = useLocalSearchParams<{ orgId?: string }>();
  const coachOrgId = params.orgId ?? null;

  // Route guard: coach_pro (or org_pro) required.
  // Unauthorized users are redirected to home instead of seeing client data.
  const { hasAccess, isLoading: accessLoading } = useCoachAccess();
  useEffect(() => {
    if (!accessLoading && !hasAccess) {
      router.replace('/(tabs)/home');
    }
  }, [accessLoading, hasAccess]);

  const [clients, setClients] = useState<ClientCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [showInvite, setShowInvite] = useState(false);

  const loadClients = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const links = await listMyClientLinks();
      const activeLinks = links.filter((l: TrainerClientLink) => l.status === 'active');
      const clientIds = activeLinks.map((l: TrainerClientLink) => l.client_profile_id);

      if (clientIds.length === 0) {
        setClients([]);
        return;
      }

      const profiles = await Promise.all(clientIds.map((id: string) => resolveProfileById(id)));
      const profileMap = new Map(profiles.filter(Boolean).map((p) => [p!.id, p!]));

      const { data: profileData } = await supabase
        .from('profiles')
        .select('id, strength_score, freshness_band, last_active, baseline_complete_pct')
        .in('id', clientIds);

      const profileDataMap = new Map((profileData || []).map((p: any) => [p.id, p]));

      const cards: ClientCardData[] = clientIds.map((id: string) => {
        const pub = profileMap.get(id);
        const pd = profileDataMap.get(id);
        return {
          id,
          name: pub?.display_name || pub?.username || 'Unknown',
          handle: pub?.username || '',
          avatar_url: null,
          strength_score: pd?.strength_score ?? 0,
          freshness_band: pd?.freshness_band ?? 'gray',
          last_active: pd?.last_active ?? '',
          baseline_pct: pd?.baseline_complete_pct ?? 0,
        };
      });

      setClients(cards);
    } catch (err) {
      console.warn('[CoachClientRoster] load error', err);
    }
  }, [profile?.id]);

  useEffect(() => {
    setLoading(true);
    loadClients().finally(() => setLoading(false));
  }, [loadClients]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadClients();
    setRefreshing(false);
  };

  const filtered = clients.filter((cl) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return cl.name.toLowerCase().includes(q) || cl.handle.toLowerCase().includes(q);
  }).sort((a, b) => {
    // Most recently active first
    const aTime = a.last_active ? new Date(a.last_active).getTime() : 0;
    const bTime = b.last_active ? new Date(b.last_active).getTime() : 0;
    return bTime - aTime;
  });

  // Guard: render nothing while loading or if access denied (redirect in effect above)
  if (accessLoading || !hasAccess) {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <ActivityIndicator style={{ flex: 1 }} color={c.accent} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => router.canGoBack() ? router.back() : router.replace('/coach-dashboard' as any)}
          style={styles.backBtn}
        >
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: c.text }]}>My Clients</Text>
        <TouchableOpacity onPress={() => setShowInvite(true)} style={styles.addBtn}>
          <UserPlus size={22} color={c.accent} />
        </TouchableOpacity>
      </View>

      {/* Client count */}
      {!loading && (
        <View style={[styles.countRow, { borderColor: c.border }]}>
          <Users size={14} color={c.textSecondary} />
          <Text style={[styles.countText, { color: c.textSecondary }]}>
            {clients.length} active client{clients.length !== 1 ? 's' : ''}
          </Text>
        </View>
      )}

      {/* Search */}
      <View style={[styles.searchRow, { borderColor: c.border, backgroundColor: c.surface }]}>
        <Search size={16} color={c.textTertiary} />
        <TextInput
          style={[styles.searchInput, { color: c.text }]}
          placeholder="Search clients..."
          placeholderTextColor={c.textTertiary}
          value={search}
          onChangeText={setSearch}
        />
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator size="large" color={c.accent} />
        </View>
      ) : clients.length === 0 ? (
        <View style={styles.center}>
          <Users size={52} color={c.textTertiary} />
          <Text style={[styles.emptyTitle, { color: c.text }]}>No clients yet.</Text>
          <Text style={{ color: c.textSecondary, fontSize: 14, textAlign: 'center', paddingHorizontal: 32, lineHeight: 20 }}>
            Invite someone to get started.
          </Text>
          <TouchableOpacity
            style={[styles.inviteBtn, { backgroundColor: c.accent, marginTop: 8 }]}
            onPress={() => setShowInvite(true)}
          >
            <Text style={[styles.inviteBtnText, { color: c.accentForeground }]}>Invite a Client</Text>
          </TouchableOpacity>
        </View>
      ) : filtered.length === 0 ? (
        <View style={styles.center}>
          <Text style={[styles.emptyTitle, { color: c.textSecondary }]}>No matching clients</Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.listContent}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />}
          renderItem={({ item: cl }) => (
            <ClientCard
              client={cl}
              onPress={() =>
                router.push({
                  pathname: '/coach-client-detail',
                  params: {
                    clientId: cl.id,
                    clientName: cl.name,
                    clientHandle: cl.handle,
                    strengthScore: String(cl.strength_score),
                    freshnessBand: cl.freshness_band,
                    baselinePct: String(cl.baseline_pct),
                  },
                } as any)
              }
            />
          )}
        />
      )}

      {/* Invite Modal */}
      {showInvite && <ClientInvite onClose={() => setShowInvite(false)} />}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
  },
  backBtn: { padding: 4 },
  headerTitle: { flex: 1, fontSize: 20, fontFamily: TYPO.heading.bold },
  addBtn: { padding: 4 },
  countRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginHorizontal: 16,
    paddingBottom: 8,
    borderBottomWidth: 1,
  },
  countText: {
    fontSize: 13,
    fontFamily: TYPO.body.regular,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginTop: 10,
    marginBottom: 8,
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 10,
    gap: 8,
  },
  searchInput: { flex: 1, height: 40, fontSize: 14, fontFamily: TYPO.body.regular },
  listContent: { paddingHorizontal: 16, paddingBottom: 32 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12 },
  emptyTitle: { fontSize: 18, fontFamily: TYPO.body.semibold },
  inviteBtn: {
    borderRadius: 2,
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  inviteBtnText: { fontFamily: TYPO.body.bold, fontSize: 14 },
});
